import { useRef, useCallback } from 'react'

interface SwipeButtonProps {
  onConfirm: () => void
  onUndo?: () => void
  disabled?: boolean
  label: string
  variant: 'green' | 'yellow'
}

const THUMB = 48
const PAD = 4
const THRESHOLD = 0.85

export function SwipeButton({ onConfirm, onUndo, disabled = false, label, variant }: SwipeButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const thumbRef = useRef<HTMLDivElement>(null)
  const fillRef = useRef<HTMLDivElement>(null)
  const undoFillRef = useRef<HTMLDivElement>(null)
  const labelRef = useRef<HTMLSpanElement>(null)
  const undoLabelRef = useRef<HTMLSpanElement>(null)
  const thumbIconRef = useRef<SVGSVGElement>(null)
  const dragging = useRef(false)
  const startX = useRef(0)
  const currentFrac = useRef(0)

  const maxTravel = () =>
    containerRef.current ? containerRef.current.offsetWidth - THUMB - PAD * 2 : 152

  const applyFrac = (frac: number) => {
    currentFrac.current = frac
    const x = frac * maxTravel()
    if (thumbRef.current) thumbRef.current.style.transform = `translateX(${x}px)`
    if (fillRef.current) fillRef.current.style.width = `${PAD + THUMB / 2 + x}px`
    if (labelRef.current) labelRef.current.style.opacity = String(Math.max(0, 1 - frac * 2))
  }

  const applyUndoFrac = (frac: number) => {
    // frac is 0..1 representing how far left the user has swiped
    const x = -frac * maxTravel()
    if (thumbRef.current) thumbRef.current.style.transform = `translateX(${x}px)`
    // Undo fill grows from the right
    if (undoFillRef.current) undoFillRef.current.style.width = `${PAD + THUMB / 2 + frac * maxTravel()}px`
    if (undoLabelRef.current) undoLabelRef.current.style.opacity = String(Math.min(1, frac * 2))
    if (labelRef.current) labelRef.current.style.opacity = String(Math.max(0, 1 - frac * 2))
    // Flip thumb arrow to point left
    if (thumbIconRef.current) {
      thumbIconRef.current.style.transform = frac > 0.2 ? 'scaleX(-1)' : 'scaleX(1)'
      thumbIconRef.current.style.color = frac > 0.2 ? '#dc2626' : ''
    }
  }

  const snapBack = () => {
    if (thumbRef.current) {
      thumbRef.current.style.transition = 'transform 0.3s ease'
      thumbRef.current.style.transform = 'translateX(0px)'
    }
    if (fillRef.current) {
      fillRef.current.style.transition = 'width 0.3s ease'
      fillRef.current.style.width = `${PAD + THUMB / 2}px`
    }
    if (undoFillRef.current) {
      undoFillRef.current.style.transition = 'width 0.3s ease'
      undoFillRef.current.style.width = '0px'
    }
    if (labelRef.current) {
      labelRef.current.style.transition = 'opacity 0.3s ease'
      labelRef.current.style.opacity = '1'
    }
    if (undoLabelRef.current) {
      undoLabelRef.current.style.transition = 'opacity 0.3s ease'
      undoLabelRef.current.style.opacity = '0'
    }
    if (thumbIconRef.current) {
      thumbIconRef.current.style.transform = 'scaleX(1)'
      thumbIconRef.current.style.color = ''
    }
  }

  const clearTransitions = () => {
    if (thumbRef.current) thumbRef.current.style.transition = 'none'
    if (fillRef.current) fillRef.current.style.transition = 'none'
    if (undoFillRef.current) undoFillRef.current.style.transition = 'none'
    if (labelRef.current) labelRef.current.style.transition = 'none'
    if (undoLabelRef.current) undoLabelRef.current.style.transition = 'none'
  }

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (disabled) return
    dragging.current = true
    startX.current = e.clientX
    clearTransitions()
    e.currentTarget.setPointerCapture(e.pointerId)
  }, [disabled])

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return
    const dx = e.clientX - startX.current
    if (onUndo && dx < 0) {
      // Left swipe — undo direction
      const frac = Math.min(1, Math.abs(dx) / maxTravel())
      applyUndoFrac(frac)
    } else {
      // Right swipe — normal confirm direction
      if (undoFillRef.current) undoFillRef.current.style.width = '0px'
      if (undoLabelRef.current) undoLabelRef.current.style.opacity = '0'
      if (thumbIconRef.current) { thumbIconRef.current.style.transform = 'scaleX(1)'; thumbIconRef.current.style.color = '' }
      const frac = Math.max(0, Math.min(1, dx / maxTravel()))
      applyFrac(frac)
    }
  }, [onUndo])

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return
    dragging.current = false
    const dx = e.clientX - startX.current
    const frac = Math.abs(dx) / maxTravel()
    if (onUndo && dx < 0 && frac >= THRESHOLD) {
      snapBack()
      currentFrac.current = 0
      onUndo()
    } else if (!onUndo || dx >= 0) {
      if (currentFrac.current >= THRESHOLD) {
        onConfirm()
      }
      snapBack()
      currentFrac.current = 0
    } else {
      snapBack()
      currentFrac.current = 0
    }
  }, [onConfirm, onUndo])

  const track = variant === 'green' ? 'bg-green-700' : 'bg-yellow-700'
  const fill  = variant === 'green' ? 'bg-green-500' : 'bg-yellow-500'
  const icon  = variant === 'green' ? 'text-green-600' : 'text-yellow-600'

  return (
    <div
      ref={containerRef}
      className={`relative w-52 h-14 rounded-full overflow-hidden select-none touch-none ${track} ${disabled ? 'opacity-40 pointer-events-none' : 'cursor-grab active:cursor-grabbing'}`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={() => { dragging.current = false; snapBack(); currentFrac.current = 0 }}
    >
      {/* Forward fill (right swipe) */}
      <div
        ref={fillRef}
        className={`absolute left-0 top-0 h-full ${fill}`}
        style={{ width: `${PAD + THUMB / 2}px`, transition: 'none', borderRadius: '9999px' }}
      />

      {/* Undo fill (left swipe) — red, grows from right */}
      <div
        ref={undoFillRef}
        className="absolute right-0 top-0 h-full bg-red-500"
        style={{ width: '0px', transition: 'none', borderRadius: '9999px' }}
      />

      {/* Forward label */}
      <span
        ref={labelRef}
        className="absolute inset-0 flex items-center justify-center text-white text-sm font-bold pointer-events-none select-none"
        style={{ paddingLeft: `${THUMB + PAD * 2}px` }}
      >
        {label}
      </span>

      {/* Undo label — fades in on left swipe */}
      <span
        ref={undoLabelRef}
        className="absolute inset-0 flex items-center justify-center text-white text-sm font-bold pointer-events-none select-none opacity-0"
        style={{ paddingRight: `${THUMB + PAD * 2}px` }}
      >
        Undo
      </span>

      {/* Draggable thumb */}
      <div
        ref={thumbRef}
        className={`absolute top-[4px] left-[4px] w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center pointer-events-none`}
        style={{ transform: 'translateX(0px)', transition: 'none' }}
      >
        <svg ref={thumbIconRef} className={`w-6 h-6 ${icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} style={{ transition: 'transform 0.15s ease' }}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  )
}
